
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { FiPlus, FiCalendar } from 'react-icons/fi';
import MenuService from '../services/menuService';
import UsageService from '../services/usageService';
import BusinessHoursService from '../services/businessHoursService';
import businessHoursEventService from '../services/businessHoursEventService';
import './Usage.css';
import { faSearch, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Check } from 'lucide-react';
import { StandardSearch, highlightMatch } from '../styles/standardStyles';
import { useTranslate } from '../translations/useTranslate';
import { useAuth } from '../contexts/AuthContext';

// Success Modal component for submission feedback
const SuccessModal = ({ show, message, onClose, t }) => {
    if (!show) return null;

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '10px'
        }} onClick={onClose}>
            <div style={{
                width: 'min(380px, 90vw)',
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                padding: '32px 24px',
                textAlign: 'center'
            }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                    <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        border: '3px solid #22c55e',
                        backgroundColor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Check size={36} color="#22c55e" strokeWidth={3} />
                    </div>
                </div>
                <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '16px',
                    color: '#1f2937'
                }}>
                    Usage Recorded Successfully
                </div>
                <button
                    onClick={onClose}
                    style={{
                        padding: '10px 24px',
                        backgroundColor: '#000000',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                    }}
                >
                    OK
                </button>
            </div>
        </div>
    );
};

// Confirmation Modal component for showing usage details before submission
const ConfirmUsageModal = ({ show, selectedItems, selectedShift, notes, totalEstimatedCost, formattedDate, onConfirm, onCancel, isSubmitting, t }) => {
    if (!show) return null;

    return (
        <div className="usage-modal-overlay" style={{ zIndex: 9999 }} onClick={!isSubmitting ? onCancel : undefined}>
            <div className="usage-modal-content usage-confirm-modal" onClick={e => e.stopPropagation()}>
                <h3>{t('usage.confirmUsageRecord')}</h3>
                
                {/* Items List */}
                <div className="usage-item-breakdown-list">
                    {selectedItems.map(item => (
                        <div className="usage-item-row" key={item.id}>
                            <span>{item.name}</span>
                            <span>x {item.quantity}</span>
                        </div>
                    ))}
                </div>

                {/* Summary Details */}
                <div className="usage-confirm-details">
                    <p><span>{t('usage.totalItems')}:</span><span>{selectedItems.reduce((sum, item) => sum + (item.quantity || 0), 0)}</span></p>
                    <div className="usage-estimated-cost-box" style={{padding:'0.2rem 0'}}>
                        <p><span style={{paddingTop:'0.1rem'}}>{t('usage.estimatedCost')}:</span><span>₹{totalEstimatedCost.toFixed(2)}</span></p>
                    </div>
                    <p><span>{t('usage.productionDate')}:</span><span>{formattedDate}</span></p>
                    <p><span>{t('usage.shift')}:</span><span>{selectedShift?.name || ''}</span></p>
                </div>

                {/* Action Buttons */}
                <div className="usage-confirm-actions">
                    <button 
                        className="usage-cancel-btn" 
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        {t('usage.cancel')}
                    </button>
                    <button 
                        className="usage-confirm-btn-final" 
                        onClick={onConfirm}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? t('usage.processing') : t('usage.confirm')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Helper Component 1: The pop-up modal for adding items ---
// Utility: normalize potential relative / invalid image URL to absolute or placeholder
const PLACEHOLDER_IMG = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"><rect width="120" height="120" rx="8" fill="%23ececec"/><text x="50%" y="55%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="%23666">No Image</text></svg>';
const FALLBACK_CHAIN = [
    (item) => item?.image,
    (item) => item?.img,
    (item) => item?.fallback_img,
    (item) => item?.placeholder_img,
];

function resolveImage(item) {
    for (const getter of FALLBACK_CHAIN) {
        const val = getter(item);
        if (val && typeof val === 'string') {
            // Trim & basic validation
            const url = val.trim();
            if (!url) continue;
            if (/^data:image\//i.test(url)) {
                return url; // data URI ok
            }
            if (/^https?:\/\//i.test(url)) {
                return url; // absolute http(s)
            }
            // Relative path -> make it relative to current origin
            const resolved = url.startsWith('/') ? url : `/${url}`;
            return resolved;
        }
    }
    return PLACEHOLDER_IMG;
}

const AddItemModal = ({ onClose, onAddItem, allItems, selectedItems }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItemIds, setSelectedItemIds] = useState(new Set());
    
    // Prevent body scroll when modal is open - removed as parent handles scroll lock
    // useEffect(() => {
    //     document.body.style.overflow = 'hidden';
    //     return () => {
    //         document.body.style.overflow = '';
    //     };
    // }, []);
    
    const filteredItems = useMemo(() => {
        if (!allItems || !Array.isArray(allItems)) return [];
        
        if (!searchTerm.trim()) {
            // Return all items sorted alphabetically when no search term
            return [...allItems].sort((a, b) => {
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
        }
        
        // Sort items: matching items first, then non-matching items
        const matchingItems = allItems.filter(item =>
            item && item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        const nonMatchingItems = allItems.filter(item =>
            item && item.name && !item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        // Sort both matching and non-matching items alphabetically
        matchingItems.sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
        
        nonMatchingItems.sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
        
        return [...matchingItems, ...nonMatchingItems];
    }, [searchTerm, allItems]);

    const handleItemClick = (item) => {
        setSelectedItemIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(item.id)) {
                newSet.delete(item.id);
            } else {
                newSet.add(item.id);
            }
            return newSet;
        });
    };

    const handleConfirm = () => {
        const itemsToAdd = allItems.filter(item => selectedItemIds.has(item.id));
        itemsToAdd.forEach(item => onAddItem(item));
        onClose();
    };

    const isItemSelected = (itemId) => selectedItemIds.has(itemId);
    const isItemAlreadyAdded = (itemId) => selectedItems.some(item => item.id === itemId);

    return (
        // New modal with new class names but same functionality
        <div className="produce-modal-overlay" onClick={onClose}>
            <div className="produce-modal" onClick={(e) => e.stopPropagation()}>
                <div className="produce-modal-header">
                    <h2 style={{ whiteSpace: 'nowrap' }}>Select Dish To Estimate</h2>
                    <div className="produce-modal-header-actions">
                        {selectedItemIds.size > 0 && (
                            <button className="produce-confirm-btn" onClick={handleConfirm} style={{ whiteSpace: 'nowrap' }}>
                                Confirm ({selectedItemIds.size})
                            </button>
                        )}
                        <button className="produce-modal-close" onClick={onClose}>×</button>
                    </div>
                </div>
                <div className="produce-modal-search">
                    <StandardSearch
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder="Search dishes..."
                        showClearButton={true}
                    />
                </div>
                <div className="produce-dishes-grid">
                    {filteredItems.length > 0 ? (
                        filteredItems.map(item => {
                            const src = resolveImage(item);
                            const isSelected = isItemSelected(item.id);
                            const isAlreadyAdded = isItemAlreadyAdded(item.id);
                            return (
                                <div 
                                    key={item.id || Math.random()} 
                                    className={`produce-dish-card ${isSelected ? 'selected' : ''} ${isAlreadyAdded ? 'already-added' : ''}`}
                                    onClick={() => !isAlreadyAdded && handleItemClick(item)}
                                >
                                    {isSelected && <div className="selection-checkmark"><FontAwesomeIcon icon={faCheck} /></div>}
                                    {isAlreadyAdded && <div className="already-added-badge">Added</div>}
                                    <img
                                        src={src}
                                        alt={item.name || 'Menu Item'}
                                        onError={(e) => { if (e.currentTarget.dataset.fallback !== '1') { e.currentTarget.dataset.fallback = '1'; e.currentTarget.src = PLACEHOLDER_IMG; } }}
                                    />
                                    <p className="produce-dish-name">{highlightMatch(item.name || 'Unknown Item', searchTerm)}</p>
                                    <p className="produce-dish-price">₹{(item.price || 0).toFixed(2)}</p>
                                </div>
                            );
                        })
                    ) : (
                        <div className="produce-empty-state">No Items Found</div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Helper Component 2: The card for each selected item ---
const UsageItemCard = ({ item, onUpdateQuantity, onRemove }) => {
    if (!item || !item.id) {
        return null;
    }
    
    const handleQuantityInputChange = (e) => {
        const value = e.target.value;
        // Parse and ensure integer only - no decimals allowed for plates
        const intValue = value === '' ? 0 : Math.round(parseFloat(value) || 0);
        onUpdateQuantity(item.id, Math.max(0, intValue));
    };
    
    // Ensure increment/decrement always use whole numbers
    const increment = () => onUpdateQuantity(item.id, Math.round(item.quantity || 0) + 1);
    const decrement = () => onUpdateQuantity(item.id, Math.max(0, Math.round(item.quantity || 0) - 1));
    
    return (
    <div className="usage-item-card">
        <img
            src={resolveImage(item)}
            alt={item.name || 'Item'}
            className="item-img"
            onError={(e) => { if (e.currentTarget.dataset.fallback !== '1') { e.currentTarget.dataset.fallback = '1'; e.currentTarget.src = PLACEHOLDER_IMG; } }}
        />
        <div className="item-details">
            <button className="remove-btn" onClick={() => onRemove(item.id)}>×</button>
            <div className="item-controls">
                <div className="quantity-control">
                    <p className="item-name">{item.name || 'Unknown Item'}</p>
                    <div className="selector">
                        <button onClick={decrement}>-</button>
                        <input 
                            type="number" 
                            value={item.quantity === 0 ? '' : Math.round(item.quantity)} 
                            placeholder="0"
                            onChange={handleQuantityInputChange}
                            step="1"
                            min="0"
                            onKeyDown={(e) => {
                                // Prevent decimal point and negative sign
                                if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === 'e' || e.key === 'E') {
                                    e.preventDefault();
                                }
                            }}
                        />
                        <button onClick={increment}>+</button>
                    </div>
                </div>
                {/* The unit dropdown is now just plain text */}
                <span className="unit-display">{item.unit || 'Plates'}</span>
            </div>
        </div>
    </div>
);
};
//this is shirath

// --- Main Usage Component ---
const Usage = ({ onItemSelected, submissionLock }) => {
    const { language, t } = useTranslate(); // Get current language and translation function
    const { user: currentUser } = useAuth();
    
    // --- Shift options loaded from database ---
    // Initialize with empty array, will be set in useEffect when translations are ready
    const [shiftOptions, setShiftOptions] = useState([]);
    
    const [selectedItems, setSelectedItems] = useState([]);
    const [notes, setNotes] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedShift, setSelectedShift] = useState(null);
    const [isShiftDropdownOpen, setShiftDropdownOpen] = useState(false);
    const [menuItems, setMenuItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // State for success modal
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    
    // State for confirmation modal
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [processingMessage, setProcessingMessage] = useState('');

    // Prevent background scroll when any modal is open (mobile-friendly)
    useEffect(() => {
        const anyModalOpen = !!(isModalOpen || showConfirmModal || processing || showSuccessModal);
        if (!anyModalOpen) return;

        const scrollY = window.scrollY || window.pageYOffset || 0;

        const prevBody = {
            position: document.body.style.position,
            top: document.body.style.top,
            width: document.body.style.width,
            overflow: document.body.style.overflow,
        };
        const prevHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            const top = document.body.style.top;
            const restoreY = top ? -parseInt(top, 10) || 0 : scrollY;

            document.body.style.position = prevBody.position;
            document.body.style.top = prevBody.top;
            document.body.style.width = prevBody.width;
            document.body.style.overflow = prevBody.overflow;
            document.documentElement.style.overflow = prevHtmlOverflow;

            window.scrollTo(0, restoreY);
        };
    }, [isModalOpen, showConfirmModal, processing, showSuccessModal]);
    
    // Load shift times from database and detect current shift
    useEffect(() => {
        const loadShifts = async () => {
            try {
                // Get today's day of week (Monday, Tuesday, etc.)
                const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                const today = new Date();
                const todayDayName = daysOfWeek[today.getDay()];
                
                
                const response = await BusinessHoursService.getShiftTimes(todayDayName);
                if (response.success && response.data && response.data.length > 0) {
                    const formattedShifts = response.data.map(shift => {
                        // Map shift_type to translation keys
                        const shiftNameMap = {
                            'Morning': t('usage.morningShift'),
                            'Afternoon': t('usage.afternoonShift'),
                            'Evening': t('usage.eveningShift'),
                            'Night': t('usage.nightShift')
                        };
                        return {
                            name: shiftNameMap[shift.shift_type] || `${shift.shift_type} Shift`,
                            time: shift.time_range,
                            shift_type: shift.shift_type,
                            opens_at: shift.opens_at,
                            closes_at: shift.closes_at
                        };
                    });
                    
                    // Add "All Day" as the first option
                    const allDayShift = {
                        name: t('usage.allDayShift'),
                        time: 'All Shifts',
                        shift_type: 'AllDay',
                        opens_at: '00:00:00',
                        closes_at: '23:59:59'
                    };
                    const shiftsWithAllDay = [allDayShift, ...formattedShifts];
                    setShiftOptions(shiftsWithAllDay);
                    
                    // Automatically detect current shift
                    try {
                        const currentShiftResponse = await BusinessHoursService.getCurrentShift();
                        if (currentShiftResponse.success && currentShiftResponse.data) {
                            const currentShift = shiftsWithAllDay.find(s => 
                                s.shift_type === currentShiftResponse.data.shift_type
                            );
                            if (currentShift) {
                                setSelectedShift(currentShift);
                            } else {
                                setSelectedShift(shiftsWithAllDay[1]); // Default to first regular shift (Morning)
                            }
                        } else {
                            setSelectedShift(shiftsWithAllDay[1]); // Default to first regular shift (Morning)
                        }
                    } catch (shiftError) {
                        console.error('Failed to detect current shift, using first regular shift:', shiftError);
                        setSelectedShift(shiftsWithAllDay[1]); // Use shiftsWithAllDay instead of formattedShifts
                    }
                    
                } else {
                    const defaultShifts = [
                        { name: t('usage.allDayShift'), time: 'All Shifts', shift_type: 'AllDay' },
                        { name: t('usage.morningShift'), time: '6:00 AM - 12:00 PM', shift_type: 'Morning' },
                        { name: t('usage.afternoonShift'), time: '12:00 PM - 6:00 PM', shift_type: 'Afternoon' },
                        { name: t('usage.eveningShift'), time: '6:00 PM - 12:00 AM', shift_type: 'Evening' }
                    ];
                    setShiftOptions(defaultShifts);
                    setSelectedShift(defaultShifts[1]); // Default to Morning
                }
            } catch (error) {
                console.error('Failed to load shift times, using defaults:', error);
                // Use default hardcoded shifts if API fails
                const defaultShifts = [
                    { name: t('usage.allDayShift'), time: 'All Shifts', shift_type: 'AllDay' },
                    { name: t('usage.morningShift'), time: '6:00 AM - 12:00 PM', shift_type: 'Morning' },
                    { name: t('usage.afternoonShift'), time: '12:00 PM - 6:00 PM', shift_type: 'Afternoon' },
                    { name: t('usage.eveningShift'), time: '6:00 PM - 12:00 AM', shift_type: 'Evening' }
                ];
                setShiftOptions(defaultShifts);
                setSelectedShift(defaultShifts[1]); // Default to Morning
            }
        };
        loadShifts();
    }, [t]); // Add 't' as dependency so shifts reload when language changes
    
    // Subscribe to business hours changes
    useEffect(() => {
        
        const unsubscribe = businessHoursEventService.subscribe(async (event) => {
            
            if (event.type === 'HOURS_UPDATED' || event.type === 'SHIFTS_REFRESHED') {
                // Refresh shift times from API
                try {
                    // Get today's day of week
                    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                    const today = new Date();
                    const todayDayName = daysOfWeek[today.getDay()];
                    
                    const response = await BusinessHoursService.getShiftTimes(todayDayName);
                    if (response.success && response.data && response.data.length > 0) {
                        const formattedShifts = response.data.map(shift => {
                            const shiftNameMap = {
                                'Morning': t('usage.morningShift'),
                                'Afternoon': t('usage.afternoonShift'),
                                'Evening': t('usage.eveningShift'),
                                'Night': t('usage.nightShift')
                            };
                            return {
                                name: shiftNameMap[shift.shift_type] || `${shift.shift_type} Shift`,
                                time: shift.time_range,
                                shift_type: shift.shift_type,
                                opens_at: shift.opens_at,
                                closes_at: shift.closes_at
                            };
                        });
                        setShiftOptions(formattedShifts);
                        
                        // Re-detect current shift after settings change
                        try {
                            const currentShiftResponse = await BusinessHoursService.getCurrentShift();
                            if (currentShiftResponse.success && currentShiftResponse.data) {
                                const currentShift = formattedShifts.find(s => 
                                    s.shift_type === currentShiftResponse.data.shift_type
                                );
                                if (currentShift) {
                                    setSelectedShift(currentShift);
                                }
                            }
                        } catch (shiftError) {
                            console.error('Failed to re-detect current shift:', shiftError);
                        }
                        
                    }
                } catch (error) {
                    console.error('Failed to refresh shift times:', error);
                }
            }
        });
        
        return () => {
            unsubscribe();
        };
    }, []);
    
    // Update shift labels when language changes
    useEffect(() => {
        setShiftOptions(prev => prev.map(shift => ({
            ...shift,
            name: t(`usage.${shift.shift_type.toLowerCase()}Shift`)
        })));
    }, [language, t]);

    const today = new Date();
    const dateOptions = { weekday: 'short', year: 'numeric', month: 'short', day: '2-digit' };
    const formattedDate = today.toLocaleDateString('en-IN', dateOptions);

    // Load menu + existing usage event (auto-shift) on mount
    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            setError(null);
            let formattedMenuItems = [];
            try {
                // Menu items - PASS LANGUAGE PARAMETER! ✅
                const menuResponse = await MenuService.getMenuItems(language, true);
                if (menuResponse.success && menuResponse.data) {
                    formattedMenuItems = menuResponse.data.map(item => ({
                        id: item.id,
                        name: item.name,
                        price: parseFloat(item.price),
                        image: item.img,
                        img: item.img,
                        fallback_img: item.fallback_img,
                        placeholder_img: item.placeholder_img,
                        image_url: item.image_url
                    }));
                    setMenuItems(formattedMenuItems);
                }
                
                // Attempt to load existing usage event for today (auto shift from backend)
                const todayIso = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
                    const eventResp = await UsageService.getUsageEvent(todayIso);
                    if (eventResp?.success && eventResp.data) {
                        const data = eventResp.data;
                        // Update shift if backend derived something
                        if (data.shift && data.shift_time) {
                            const matchShift = shiftOptions.find(s => s.name === data.shift);
                            if (matchShift) setSelectedShift(matchShift);
                            else setSelectedShift({ name: data.shift, time: data.shift_time });
                        }
                        if (data.exists && Array.isArray(data.items) && data.items.length > 0) {
                            const mapped = data.items.map(it => {
                                // Find the full menu item to get image properties
                                const fullItem = formattedMenuItems.find(mi => mi.id === it.menu_item_id);
                            return {
                                id: it.menu_item_id,
                                name: it.name,
                                price: parseFloat(it.price || 0),
                                quantity: Math.round(it.quantity || 0), // Round to integer for plates
                                unit: it.unit || 'Plates',
                                // Preserve image properties
                                image: fullItem?.image,
                                img: fullItem?.img,
                                fallback_img: fullItem?.fallback_img,
                                placeholder_img: fullItem?.placeholder_img,
                                image_url: fullItem?.image_url
                            };
                        });
                        setSelectedItems(mapped);
                        // Notes are NOT loaded from existing events
                    } else {
                        // Fallback to draft if no existing event
                        const savedDraft = localStorage.getItem('usageDraft');
                        if (savedDraft) {
                            try {
                                const draft = JSON.parse(savedDraft);
                                if (draft && draft.selectedItems && Array.isArray(draft.selectedItems)) {
                                    // Merge draft items with current menu items to get fresh image URLs
                                    const itemsWithImages = draft.selectedItems.map(draftItem => {
                                        const fullItem = formattedMenuItems.find(mi => mi.id === draftItem.id);
                                        return {
                                            ...draftItem,
                                            image: fullItem?.image,
                                            img: fullItem?.img,
                                            fallback_img: fullItem?.fallback_img,
                                            placeholder_img: fullItem?.placeholder_img,
                                            image_url: fullItem?.image_url
                                        };
                                    });
                                    setSelectedItems(itemsWithImages);
                                    // Notes are NOT loaded from draft
                                    if (draft.shift) {
                                        const foundShift = shiftOptions.find(s => s.name === draft.shift);
                                        if (foundShift) setSelectedShift(foundShift);
                                    }
                                } else {
                                    localStorage.removeItem('usageDraft');
                                }
                            } catch (_) {
                                localStorage.removeItem('usageDraft');
                            }
                        }
                    }
                }
            } catch (err) {
                console.error('Init load error:', err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };
        init();
    }, [language]); // ✅ Reload when language changes!

    // Smart loader: When shift changes, load event first, then templates as fallback
    useEffect(() => {
        const loadShiftDataSmart = async () => {
            if (!selectedShift?.shift_type || !menuItems.length) return;
            
            // Skip this useEffect for All Day shift - it's handled by the dedicated All Day useEffect below
            if (selectedShift.shift_type === 'AllDay') {
                return;
            }
            
            try {
                // STEP 1: Check if there's an existing usage event for today + this shift
                const todayIso = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
                // Build complete shift string: "Morning Shift (6:00 AM - 12:00 PM)"
                const completeShift = selectedShift?.name && selectedShift?.time 
                    ? `${selectedShift.name} (${selectedShift.time})`
                    : selectedShift?.name || '';
                const eventResp = await UsageService.getUsageEvent(todayIso, completeShift);
                
                if (eventResp?.success && eventResp.data?.exists && eventResp.data.items?.length > 0) {
                    // Found an existing event - load it
                    const mapped = eventResp.data.items.map(it => {
                        const fullItem = menuItems.find(mi => mi.id === it.menu_item_id);
                        return {
                            id: it.menu_item_id,
                            name: it.name,
                            price: parseFloat(it.price || 0),
                            quantity: Math.round(it.quantity || 0), // Round to integer for plates
                            unit: it.unit || 'Plates',
                            image: fullItem?.image,
                            img: fullItem?.img,
                            fallback_img: fullItem?.fallback_img,
                            placeholder_img: fullItem?.placeholder_img,
                            image_url: fullItem?.image_url
                        };
                    });
                    setSelectedItems(mapped);
                    if (eventResp.data.notes) setNotes(eventResp.data.notes);
                    return; // Don't load templates if event exists
                }
                
                // STEP 2: No event found - load templates from previous days
                const token = localStorage.getItem('token');
                if (!token) return;

                const payload = JSON.parse(atob(token.split('.')[1]));
                const businessId = payload.businessId;

                const templateResp = await fetch(`/api/usage-templates/by-shift/${businessId}/${selectedShift.shift_type}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (templateResp.ok) {
                    const data = await templateResp.json();
                    if (data.success && data.data.templates.length > 0) {
                        const templatedItems = data.data.templates.map(template => {
                            const fullItem = menuItems.find(mi => mi.id === template.menu_item_id);
                            return {
                                id: template.menu_item_id,
                                name: template.menu_item_name,
                                price: parseFloat(template.price || 0),
                                quantity: template.quantity,
                                unit: 'Plates',
                                image: fullItem?.image,
                                img: fullItem?.img,
                                fallback_img: fullItem?.fallback_img,
                                placeholder_img: fullItem?.placeholder_img,
                                image_url: fullItem?.image_url || template.image_url
                            };
                        });
                        setSelectedItems(templatedItems);
                    } else {
                        // No templates found - clear items
                        setSelectedItems([]);
                    }
                }
            } catch (error) {
                console.error('Error loading shift data:', error);
            }
        };

        loadShiftDataSmart();
    }, [selectedShift?.shift_type, menuItems.length]); // Single source of truth

    const totalEstimatedCost = useMemo(() => {
        if (!selectedItems || !Array.isArray(selectedItems)) return 0;
        return selectedItems.reduce((total, item) => {
            const price = item && item.price ? parseFloat(item.price) : 0;
            const quantity = item && item.quantity ? parseInt(item.quantity, 10) : 0;
            return total + (price * quantity);
        }, 0);
    }, [selectedItems]);

    const handleUpdateQuantity = useCallback((itemId, newQuantity) => {
        // Always ensure quantity is a non-negative integer (no decimals for plates)
        const intQuantity = Math.max(0, Math.round(parseFloat(newQuantity) || 0));
        setSelectedItems(currentItems =>
            currentItems.map(item =>
                item.id === itemId ? { ...item, quantity: intQuantity } : item
            )
        );
    }, []);
    
    const handleRemoveItem = useCallback((itemId) => {
        setSelectedItems(currentItems => currentItems.filter(item => item.id !== itemId));
    }, []);

    const handleAddItem = useCallback((itemToAdd) => {
        if (selectedItems.find(item => item.id === itemToAdd.id)) return;
        // Always start with integer quantity of 1
        setSelectedItems(currentItems => [...currentItems, { ...itemToAdd, quantity: 1, unit: 'Plates' }]);
        setIsModalOpen(false);
        
        // Trigger the 10-second lock when an item is added
        if (onItemSelected) {
            onItemSelected(selectedShift);
        }
    }, [selectedItems, onItemSelected, selectedShift]);

    // Automatically trigger submission lock when items are populated (e.g. from draft or suggestions)
    useEffect(() => {
        if (selectedItems.length > 0 && onItemSelected) {
            if (!submissionLock || !submissionLock.isActive) {
                onItemSelected(selectedShift);
            }
        }
    }, [selectedItems.length, onItemSelected, selectedShift, submissionLock]);
    
    // Watch for shift changes and reload data for "All Day" shift
    useEffect(() => {
        const loadShiftData = async () => {
            // Only run if "All Day" shift is selected
            if (!selectedShift || selectedShift.shift_type !== 'AllDay') { 
                return;
            }
            
            const todayIso = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
            
            try {
                // For All Day, pass simple name as backend has special handling
                const eventResp = await UsageService.getUsageEvent(todayIso, 'All Day');
                
                if (eventResp?.success && eventResp.data) {
                    const data = eventResp.data;
                    
                    if (data.items && Array.isArray(data.items) && data.items.length > 0) {
                        // Map items with full menu item data for images
                        const mapped = data.items.map(it => {
                            const fullItem = menuItems.find(mi => mi.id === it.menu_item_id);
                            return {
                                id: it.menu_item_id,
                                name: it.name,
                                price: parseFloat(it.price || 0),
                                quantity: Math.round(parseFloat(it.quantity || it.total_quantity || 0)), // Round to integer
                                unit: it.unit || 'Plates',
                                image: fullItem?.image,
                                img: fullItem?.img,
                                fallback_img: fullItem?.fallback_img,
                                placeholder_img: fullItem?.placeholder_img,
                                image_url: it.image_url || fullItem?.image_url,
                                shifts_used: it.shifts_used, // Extra info for All Day
                                event_count: it.event_count
                            };
                        });
                        setSelectedItems(mapped);
                        
                        // Set notes to show it's aggregated
                        const noteText = data.notes || `Aggregated from all shifts`;
                        setNotes(noteText);
                    } else {
                        setSelectedItems([]);
                        setNotes('');
                    }
                } else {
                    setSelectedItems([]);
                    setNotes('');
                }
            } catch (error) {
                console.error('❌ Error loading All Day data:', error);
                setSelectedItems([]);
                setNotes('');
            }
        };
        
        // Only run if we have menu items loaded and shift is All Day
        if (menuItems.length > 0 && selectedShift && selectedShift.shift_type === 'AllDay') {
            loadShiftData();
        }
    }, [selectedShift, menuItems, t]);
    
    const handleShiftSelect = (shift) => {
        setSelectedShift(shift);
        setShiftDropdownOpen(false);
    };

    const handleSubmit = async () => {
        if (!selectedItems || selectedItems.length === 0) {
            alert(t('usage.pleaseAddItems'));
            return;
        }
        
        const invalidItems = selectedItems.filter(item => !item || !item.quantity || item.quantity <= 0);
        if (invalidItems.length > 0) {
            alert(t('usage.invalidQuantities'));
            return;
        }
        
        // Show confirmation modal instead of direct submission
        setShowConfirmModal(true);
    };

    const handleConfirmSubmission = async () => {
        // Close confirmation modal and start processing
        setShowConfirmModal(false);
        setIsSubmitting(true);
        setProcessing(true);
        setProcessingProgress(0);
        setProcessingMessage('Preparing usage record...');
        setError(null);
        
        try {
            setProcessingProgress(30);
            setProcessingMessage('Recording usage data...');
            
            const usageData = {
                items: selectedItems.map(item => ({
                    menu_item_id: item.id,
                    quantity: parseInt(item.quantity, 10) || 0,
                    unit: item.unit || t('usage.plates'),
                    estimated_cost: (parseFloat(item.price) || 0) * (parseInt(item.quantity, 10) || 0)
                })),
                recorded_by_user_id: currentUser?.userId || currentUser?.user_id || currentUser?.id || null,
                production_date: today.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
                shift: selectedShift ? selectedShift.name : t('usage.morningShift'),
                shift_time: selectedShift ? selectedShift.time : '6:00 AM - 12:00 PM',
                notes: notes || '',
                total_estimated_cost: totalEstimatedCost
            };
            
            localStorage.setItem('usageDraft', JSON.stringify({
                selectedItems,
                // Notes are NOT saved in draft
                shift: selectedShift ? selectedShift.name : t('usage.morningShift'),
            }));
            
            setProcessingProgress(60);
            setProcessingMessage('Saving usage record...');
            
            const response = await UsageService.recordUsage(usageData);
            
            if (response && response.success) {
                setProcessingProgress(100);
                setProcessingMessage('Complete!');
                
                // Dispatch custom event to clear daily operations lock immediately
                window.dispatchEvent(new CustomEvent('daily-operations:refresh'));
                
                // Show success modal after brief delay
                setTimeout(() => {
                    setProcessing(false);
                    setProcessingProgress(0);
                    setProcessingMessage('');
                    setSuccessMessage(t('usage.successMessage'));
                    setShowSuccessModal(true);
                }, 1000);
                
                // Clear the form data
                setSelectedItems([]);
                setNotes('');
                localStorage.removeItem('usageDraft');
            } else {
                throw new Error((response && response.message) || 'Failed to submit usage data');
            }
        } catch (err) {
            const errorMessage = err.message || 'An unexpected error occurred';
            setError(errorMessage);
            alert(`Error: ${errorMessage}`);
            setProcessing(false);
            setProcessingProgress(0);
            setProcessingMessage('');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="usage-page-container">
            {error && <div className="error-message">Error: {error}</div>}
            {isModalOpen && (
                <AddItemModal onClose={() => setIsModalOpen(false)} onAddItem={handleAddItem} allItems={menuItems} selectedItems={selectedItems} />
            )}
            
            {/* Success Modal for submission feedback */}
            <SuccessModal
                show={showSuccessModal}
                message={successMessage}
                onClose={() => setShowSuccessModal(false)}
                t={t}
            />
            
            {/* Processing Modal */}
            {processing && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: '10px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '40px',
                        maxWidth: '400px',
                        width: '90%',
                        textAlign: 'center',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                    }}>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #000',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 20px'
                        }}></div>
                        
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '21px', color: '#333' }}>
                            Processing Usage Record
                        </h3>
                        
                        <p style={{
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '20px',
                            minHeight: '20px'
                        }}>
                            {processingMessage}
                        </p>
                        
                        <div style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            marginBottom: '10px'
                        }}>
                            <div style={{ 
                                width: `${processingProgress}%`,
                                height: '100%',
                                backgroundColor: '#000',
                                transition: 'width 0.3s ease'
                            }}></div>
                        </div>
                        
                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#000', margin: 0 }}>
                            {processingProgress.toFixed(0)}% Complete
                        </p>
                    </div>
                </div>
            )}
            
            {/* Confirmation Modal for usage details */}
            <ConfirmUsageModal
                show={showConfirmModal}
                selectedItems={selectedItems}
                selectedShift={selectedShift}
                notes={notes}
                totalEstimatedCost={totalEstimatedCost}
                formattedDate={formattedDate}
                onConfirm={handleConfirmSubmission}
                onCancel={() => setShowConfirmModal(false)}
                isSubmitting={isSubmitting}
                t={t}
            />
            <div className="usage-form">
                <div className="form-group">
                    <div className="date-input">
                        <FiCalendar className="input-icon" />
                        <div className="input-text-wrapper">
                            <span style={{ fontSize: '0.8rem' }}>{t('usage.productionDate')}</span>
                            <span>{formattedDate}</span>
                        </div>
                    </div>
                </div>
                <div className="form-group relative">
                    <div className="shift-select" onClick={() => setShiftDropdownOpen(!isShiftDropdownOpen)}>
                        <div className="input-text-wrapper">
                            <span className="shift-details">{selectedShift?.name || t('usage.selectShift')} <span className="shift-time">{selectedShift?.time || ''}</span></span>
                        </div>
                            <span className="chevron-down" />
                    </div>
                    {isShiftDropdownOpen && (
                        <div className="shift-dropdown-options">
                            {shiftOptions.map((option) => (
                                <div key={option.name} className="shift-option" onClick={() => handleShiftSelect(option)}>
                                    <span className="shift-name">{option.name}</span>
                                    <span className="shift-time">{option.time}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className="items-section">
                <h3>{t('usage.items')}</h3>
                {selectedItems.length === 0 ? (
                    <button className="add-more-items-btn" onClick={() => setIsModalOpen(true)}>
                        <FiPlus /> {t('usage.addItems')}
                    </button>
                ) : (
                    <>
                        <div className="items-list">
                            {selectedItems.map(item => (
                                <UsageItemCard key={item.id} item={item} onUpdateQuantity={handleUpdateQuantity} onRemove={handleRemoveItem} />
                            ))}
                        </div>
                        <button className="add-more-items-btn" onClick={() => setIsModalOpen(true)}>
                            <FiPlus /> {t('usage.addMoreItems')}
                        </button>
                        <div className="total-cost-summary">
                            <p>{t('usage.estimatedCost')}: ₹{totalEstimatedCost.toFixed(2)}</p>
                        </div>
                    </>
                )}
            </div>
            <div className="notes-section">
                <h3>{t('usage.notes')}</h3>
                <textarea 
                    value={notes} 
                    onChange={(e) => setNotes(e.target.value)} 
                    placeholder="enter notes"
                />
            </div>
            
            <div className="form-actions">
                <button 
                    className="submit-btn" 
                    onClick={handleSubmit}
                    disabled={
                        selectedItems.length === 0 || 
                        selectedItems.some(item => !item.quantity || item.quantity <= 0) ||
                        (submissionLock && submissionLock.isActive && !submissionLock.canSubmit)
                    }
                    style={{
                        opacity: (selectedItems.length === 0 || selectedItems.some(item => !item.quantity || item.quantity <= 0) || (submissionLock && submissionLock.isActive && !submissionLock.canSubmit)) ? 0.6 : 1,
                        cursor: (selectedItems.length === 0 || selectedItems.some(item => !item.quantity || item.quantity <= 0) || (submissionLock && submissionLock.isActive && !submissionLock.canSubmit)) ? 'not-allowed' : 'pointer'
                    }}
                >
                    {(submissionLock && submissionLock.isActive && !submissionLock.canSubmit) 
                        ? `Wait ${submissionLock.secondsRemaining}s` 
                        : t('usage.submit')}
                </button>
            </div>
        </div>
    );
};

export default Usage;